import type { I18nMessageObject } from '@_/types';
import type { DialogControlProps } from '@_/ui';
import type { UseCommentMutationOptions } from './useCommentMutation.ts';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import {
  Avatar,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorMessage,
  Text
} from '@_/ui';
import { i18nMessages } from '../../shared/i18nMessages.ts';
import { CommentBodyWrapper } from '../CommentBody/index.ts';
import { CommentContent, Editable, EditorProvider, useCreateEditor } from '../Editor/index.ts';
import { I18nContent } from '../I18nContent/index.ts';
import { useCommentMutation } from './useCommentMutation.ts';

type CommentEditorDialogProps<TComment> = DialogControlProps &
  Pick<UseCommentMutationOptions<TComment>, 'createComment' | 'onSuccess'> & {
    author: {
      name: I18nMessageObject | string;
      avatarUrl: string | null;
    };
    parentCommentId?: string;
    postId: string;
  };

export function CommentEditorDialog<TComment>({
  author,
  createComment,
  onOpenChange,
  onSuccess,
  parentCommentId,
  postId,
  ...dialogProps
}: CommentEditorDialogProps<TComment>) {
  const intl = useIntl();

  const mutation = useCommentMutation({
    createComment,
    onSuccess: async data => {
      onOpenChange(false);
      onSuccess?.(data);
    }
  });

  const { editor, isValid, value } = useCreateEditor({
    contentConstructor: CommentContent,
    createDefaultContent: CommentContent.createDefaultContent,
    disabled: mutation.isPending,
    extensions: CommentContent.extensions,
    placeholder: intl.formatMessage(i18nMessages.writeComment),
    validator: CommentContent.validate
  });

  return (
    <Dialog
      disablePointerDismissal={mutation.isPending}
      onOpenChange={onOpenChange}
      showSwipeHandle
      {...dialogProps}
    >
      <DialogHeader>
        <DialogTitle>
          {parentCommentId ? (
            <FormattedMessage
              id="component:heading.reply-to-comment"
              defaultMessage="Reply to comment"
            />
          ) : (
            <FormattedMessage id="component:heading.post-comment" defaultMessage="Post a comment" />
          )}
        </DialogTitle>
      </DialogHeader>

      {mutation.isError && (
        <div className="mb-6">
          <ErrorMessage />
        </div>
      )}

      <div
        className={clsx(
          'overflow-hidden rounded-md border border-gray-300 shadow-xs',
          'focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
        )}
      >
        <div className="px-3 pt-3 pb-1">
          <div className="flex items-center gap-x-2">
            <Avatar className="size-6" name={author?.name} src={author?.avatarUrl} />
            <Text size="xs" subdued>
              <FormattedMessage
                {...i18nMessages.commentAsUser}
                values={{
                  b: msg => (
                    <Text size="xs" semibold>
                      {msg}
                    </Text>
                  ),
                  name:
                    typeof author?.name === 'string' ? (
                      author?.name
                    ) : (
                      <I18nContent i18n={author?.name} />
                    )
                }}
              />
            </Text>
          </div>
        </div>
        <div
          className="h-24 cursor-text overflow-y-auto bg-white px-4 py-3"
          onClick={() => editor?.commands.focus()}
        >
          <CommentBodyWrapper>
            {editor && (
              <EditorProvider editor={editor}>
                <Editable autoFocus />
              </EditorProvider>
            )}
          </CommentBodyWrapper>
        </div>
      </div>

      <DialogFooter>
        <Button
          color="primary"
          disabled={!isValid || mutation.isSuccess}
          fillContainer
          loading={mutation.isPending}
          onClick={() =>
            mutation.mutateAsync({
              contentJson: value,
              postId,
              parentCommentId
            })
          }
        >
          {parentCommentId ? (
            <FormattedMessage {...buttonCommonI18n.reply} />
          ) : (
            <FormattedMessage {...buttonCommonI18n.comment} />
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
